import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Camera, CheckCircle2, XCircle, Circle, ChevronRight } from "lucide-react";

const RESULT_META = {
  pass: { color: "bg-primary text-primary-foreground", Icon: CheckCircle2, label: "PASS" },
  fail: { color: "bg-red-700 text-white", Icon: XCircle, label: "FAIL" },
  na:   { color: "bg-secondary text-foreground", Icon: Circle, label: "N/A" },
  pending: { color: "bg-amber-500 text-white", Icon: Circle, label: "PENDING" },
};

export default function InspectorDashboard() {
  const [inspections, setInspections] = useState([]);
  const [active, setActive] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/inspections/mine");
      setInspections(r.data);
      if (active) {
        const fresh = r.data.find(i => i.id === active.id);
        setActive(fresh || null);
      }
    } catch { }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="kicker">Inspector Portal</div>
          <h1 className="display text-4xl">Assigned inspections</h1>
        </div>

        {!active ? (
          <div className="grid gap-3">
            {inspections.length === 0 ? (
              <div className="p-10 border border-border text-center">
                <div className="display text-xl">Nothing assigned yet</div>
                <p className="text-muted-foreground text-sm mt-1">An admin will assign vehicles once sellers request verification.</p>
              </div>
            ) : inspections.map((i) => {
              const veh = i.vehicle || {};
              const done = (i.checklist || []).filter(c => c.result !== "pending").length;
              const total = (i.checklist || []).length;
              return (
                <button key={i.id} onClick={() => setActive(i)} className="border border-border bg-card p-4 text-left hover:border-primary transition-colors flex items-center justify-between" data-testid={`inspector-open-${i.id}`}>
                  <div>
                    <div className="display text-lg">{veh.year} {veh.make} {veh.model}</div>
                    <div className="text-xs text-muted-foreground mono">{veh.city} · {veh.id}</div>
                  </div>
                  <div className="text-right">
                    <div className="mono text-xs uppercase tracking-widest text-primary">{i.status.replace("_", " ")}</div>
                    <div className="text-sm text-muted-foreground">{done}/{total} items complete</div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        ) : (
          <InspectionEditor inspection={active} onBack={() => { setActive(null); load(); }} onChange={load} />
        )}
      </div>
      <Footer />
    </div>
  );
}

function InspectionEditor({ inspection, onBack, onChange }) {
  const veh = inspection.vehicle || {};
  const [items, setItems] = useState(inspection.checklist || []);
  const [busy, setBusy] = useState(null);
  const [summary, setSummary] = useState(inspection.summary || "");
  const [verdict, setVerdict] = useState("pass");
  const [submitOpen, setSubmitOpen] = useState(false);
  const done = items.filter(c => c.result !== "pending").length;
  const total = items.length;
  const editable = inspection.status === "open" || inspection.status === "in_progress";

  const updateItem = async (key, patch) => {
    const next = items.map((it) => it.key === key ? { ...it, ...patch } : it);
    setItems(next);
    const target = next.find(it => it.key === key);
    setBusy(key);
    try {
      await api.patch(`/inspections/${inspection.id}/item`, {
        key,
        result: target.result,
        notes: target.notes || "",
        photo_file_ids: target.photo_file_ids || [],
      });
    } catch (e) {
      toast.error("Failed to save");
    } finally { setBusy(null); }
  };

  const uploadForItem = async (key, files) => {
    setBusy(key);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("module_key", "auto");
        fd.append("case_id", inspection.id);
        fd.append("category", `vehicle.${key}`);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      }
      const item = items.find(i => i.key === key);
      const merged = [...(item.photo_file_ids || []), ...ids];
      await updateItem(key, { photo_file_ids: merged });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(null); }
  };

  const submit = async () => {
    try {
      await api.post(`/inspections/${inspection.id}/submit`, { summary, verdict });
      toast.success(`Inspection submitted — verdict ${verdict.toUpperCase()}`);
      setSubmitOpen(false);
      onBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    }
  };

  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-primary" data-testid="inspector-back-btn">← Back to inspections</button>
      <div className="mt-3 flex items-baseline justify-between gap-4">
        <div>
          <h2 className="display text-3xl">{veh.year} {veh.make} {veh.model}</h2>
          <div className="mono text-xs text-muted-foreground">{veh.city} · {veh.id}</div>
        </div>
        <div className="text-right">
          <div className="kicker">Progress</div>
          <div className="display text-3xl">{done}/{total}</div>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {items.map((it) => {
          const meta = RESULT_META[it.result] || RESULT_META.pending;
          return (
            <div key={it.key} className="border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-semibold">{it.label}</div>
                  {it.photo_file_ids?.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {it.photo_file_ids.map((fid) => (
                        <img key={fid} src={`${API_BASE}/files/${fid}?v=thumb`} className="h-14 w-14 object-cover border border-border" alt="" />
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1">
                  {["pass", "fail", "na"].map((r) => (
                    <button
                      key={r}
                      disabled={!editable || busy === it.key}
                      onClick={() => updateItem(it.key, { result: r })}
                      className={`min-w-[84px] min-h-[40px] px-3 text-xs font-bold uppercase tracking-widest rounded-sm border transition-colors ${
                        it.result === r ? RESULT_META[r].color : "bg-background hover:bg-secondary border-border"
                      }`}
                      data-testid={`item-${it.key}-${r}`}
                    >
                      {r === "na" ? "N/A" : r}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-3 grid md:grid-cols-3 gap-2">
                <Textarea
                  disabled={!editable}
                  rows={2}
                  placeholder="Inspector notes (optional)"
                  value={it.notes}
                  onChange={(e) => setItems(items.map(x => x.key === it.key ? { ...x, notes: e.target.value } : x))}
                  onBlur={() => updateItem(it.key, {})}
                  className="md:col-span-2"
                  data-testid={`item-${it.key}-notes`}
                />
                <label className={`btn-outline cursor-pointer justify-center ${!editable ? "opacity-50 pointer-events-none" : ""}`} data-testid={`item-${it.key}-upload`}>
                  <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => e.target.files && uploadForItem(it.key, Array.from(e.target.files))} />
                  <Camera className="h-4 w-4" /> {busy === it.key ? "Working…" : "Add photo"}
                </label>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <meta.Icon className="h-4 w-4" /><span className="text-xs mono uppercase tracking-widest">{meta.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {editable && (
        <div className="sticky bottom-0 mt-6 -mx-6 px-6 py-4 bg-background border-t border-border flex justify-end">
          <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" disabled={done < total} data-testid="inspector-submit-btn">
                Submit Inspection
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Submit inspection</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <div className="kicker mb-1">Verdict</div>
                  <Select value={verdict} onValueChange={setVerdict}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pass">PASS — issue THOM Verified™</SelectItem>
                      <SelectItem value="fail">FAIL — reject verification</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="kicker mb-1">Inspector summary</div>
                  <Textarea rows={5} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="inspector-summary-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} className="bg-primary hover:bg-primary/90" data-testid="inspector-confirm-submit">Confirm submission</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
